import { Row, Table } from "@tanstack/react-table";
import { X } from "lucide-react";

import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";

import { DataTableFacetedFilter } from "./data-table-faceted-filter";
import { DataTableViewOptions } from "@components/ui/data-table-view-options";
import { InputHTMLAttributes, useEffect, useState } from "react";

/**
 * filterFn to set on any column driven by a faceted (multiple selection) filter:
 * the row is kept when its value is one of the selected ones. An empty selection
 * filters nothing — the toolbar clears the filter rather than passing an empty list.
 */
export const inSelection = <TData,>(row: Row<TData>, id: string, value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) return true;
  return value.includes(row.getValue(id));
};

export type DataTableFilter = {
  label: string;
  value: string;
  options: {
    value: string;
    label: string;
    badgeLabel?: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
};

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  displayDataViewOptions?: boolean;
  searchPlaceHolder?: string;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  filters?: DataTableFilter[];
  children?: React.ReactNode;
}

export function DataTableToolbar<TData>({
  table,
  displayDataViewOptions = false,
  searchPlaceHolder,
  globalFilter,
  setGlobalFilter,
  filters,
  children,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  return (
    <div className="flex items-center justify-between py-4 my-0">
      <div className="flex flex-1 items-center gap-4">
        <DebouncedInput
          value={globalFilter ?? ""}
          onChange={value => setGlobalFilter(String(value))}
          className="h-8 w-[150px] lg:w-[250px]"
          placeholder={searchPlaceHolder}
        />
        {children}
        {filters &&
          filters.map(filter => {
            return (
              table.getColumn(filter.value) && (
                <DataTableFacetedFilter
                  key={filter.value}
                  column={table.getColumn(filter.value)}
                  title={filter.label}
                  options={filter.options}
                />
              )
            );
          })}
        {isFiltered && (
          <Button variant="ghost" onClick={() => table.resetColumnFilters()} className="h-8 px-2 lg:px-3">
            Réinitialiser
            <X />
          </Button>
        )}
      </div>
      {displayDataViewOptions && <DataTableViewOptions table={table} />}
    </div>
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return <Input {...props} value={value} onChange={e => setValue(e.target.value)} />;
}
